import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight, ChevronDown, ChevronLeft, ChevronRight, Lock, MapPin, Palmtree, Wallet, Users,
  FileText, Ticket, UserCheck, Gift, Loader2, Check, Sun, ShieldCheck, Home,
} from 'lucide-react';
import { useSettings } from '../../SettingsContext';
import {
  createLead, fireEvent, getLpConfig, initPixels, maskPhone, optimizedImage, optimizedSrcSet,
  phoneValid, setMeta, trackStep, UFS,
} from '../../lib/lp';

const NAVY = '#0c2a5c';
const NAVY_DK = '#081c40';
const YELLOW = '#FFE000';
// Foto do hero fixa: o index.html a pré-carrega antes do JavaScript (LCP).
const HERO_SRC = '/uploads/cortesia/cortesia_49fe74b502a4.jpg';
const HERO_SIZES = '(min-width: 1024px) 100vw, 100vw';

const DEFAULTS = { formFields: null, priceLabel: 'R$ 38,90', diariasMax: 4, minPessoas: 4, validadeAte: 'novembro de 2028', destinos: [], whatsapp: null, tracking: {}, privacyUrl: '/privacidade', social: { count: 20000 } };

const useMediaQuery = (query) => {
  const [match, setMatch] = useState(() => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false));
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setMatch(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [query]);
  return match;
};

const reducedMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// ------------------------------------------------------------------ formulário
// Os campos vêm do admin (cfg.formFields); estes são os padrões usados até a configuração carregar.
const DEFAULT_FIELDS = [
  { key: 'name', type: 'name', label: 'Seu primeiro nome', placeholder: 'Como podemos chamar você?', required: true, step: 1 },
  { key: 'whatsapp', type: 'phone', label: 'WhatsApp com DDD', placeholder: '(00) 00000-0000', required: true, step: 1 },
  { key: 'cityuf', type: 'cityuf', label: 'Cidade e Estado', placeholder: 'Ex.: Curitiba', required: true, step: 1 },
  {
    key: 'quando', type: 'choice', label: 'Quando você pretende viajar?', required: true, step: 2,
    options: [{ v: '3m', label: 'Nos próximos 3 meses' }, { v: '6m', label: 'Nos próximos 6 meses' }, { v: 'indefinido', label: 'Ainda não tenho uma data definida' }],
  },
  {
    key: 'pessoas', type: 'choice', label: 'Quantas pessoas viajariam com você?', required: true, step: 2,
    options: [{ v: 'ate4', label: 'Até 4 pessoas' }, { v: '5a6', label: '5 a 6 pessoas' }, { v: '7mais', label: '7 ou mais pessoas' }],
  },
];

const fieldErrors = (fields, v) => {
  const e = {};
  fields.forEach((f) => {
    const val = v[f.key] ?? '';
    if (f.type === 'name') {
      if (!/^[A-Za-zÀ-ÖØ-öø-ÿ'’\- ]{2,40}$/.test(v.name.trim())) e.name = 'Como podemos chamar você?';
    } else if (f.type === 'phone') {
      if (!phoneValid(v.whatsapp)) e.whatsapp = 'Confira o WhatsApp com DDD';
    } else if (f.type === 'cityuf') {
      if (f.required || v.city.trim() || v.uf) {
        if (v.city.trim().length < 2) e.city = 'Informe sua cidade';
        if (!v.uf) e.uf = 'Estado';
      }
    } else if (f.type === 'text') {
      if (f.required && !String(val).trim()) e[f.key] = 'Preencha este campo';
    } else if (f.type === 'choice') {
      if (f.required && !val) e[f.key] = 'Escolha uma opção';
    }
  });
  if (!v.consent) e.consent = 'Para gerar seu código, aceite o contato pelo WhatsApp';
  return e;
};

const keysOf = (f) => (f.type === 'cityuf' ? ['city', 'uf'] : [f.key]);

const useLeadForm = (navigate, fields) => {
  const steps = useMemo(() => [1, 2].filter((n) => fields.some((f) => f.step === n)), [fields]);
  const [stepIdx, setStepIdx] = useState(0);
  const [values, setValues] = useState({ name: '', whatsapp: '', city: '', uf: '', consent: false });
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [duplicate, setDuplicate] = useState(false);
  const startedAt = useRef(0);
  const started = useRef(false);
  const honeypot = useRef('');
  const errors = useMemo(() => fieldErrors(fields, values), [fields, values]);
  const idx = Math.min(stepIdx, steps.length - 1);
  const step = steps[idx];
  const isLast = idx >= steps.length - 1;
  const stepFields = useMemo(() => fields.filter((f) => f.step === step), [fields, step]);

  const interact = useCallback(() => {
    if (started.current) return;
    started.current = true;
    startedAt.current = Date.now();
    trackStep('start');
    fireEvent('FormStart');
  }, []);

  const set = (key, value) => { interact(); setValues((p) => ({ ...p, [key]: value })); };
  const blur = (key) => setTouched((p) => ({ ...p, [key]: true }));
  const markBad = (bad) => setTouched((p) => ({ ...p, ...Object.fromEntries(bad.map((k) => [k, true])) }));

  const next = () => {
    const bad = stepFields.flatMap(keysOf).filter((k) => errors[k]);
    if (bad.length) { markBad(bad); return; }
    setStepIdx(idx + 1);
    if (idx === 0) { trackStep('step2'); fireEvent('FormStep2'); }
  };

  const submit = async () => {
    if (submitting) return;
    const bad = [...stepFields.flatMap(keysOf), 'consent'].filter((k) => errors[k]);
    if (bad.length) { markBad(bad); return; }
    const earlier = fields.filter((f) => f.step < step).flatMap(keysOf).filter((k) => errors[k]);
    if (earlier.length) { markBad(earlier); setStepIdx(0); return; }
    setSubmitting(true);
    setError('');
    setDuplicate(false);
    try {
      const payload = { name: values.name.trim(), whatsapp: values.whatsapp, city: values.city.trim(), uf: values.uf, consent: true, extra: {} };
      fields.forEach((f) => {
        if (f.type !== 'choice' && f.type !== 'text') return;
        if (f.key === 'quando' || f.key === 'pessoas') payload[f.key] = values[f.key] || '';
        else payload.extra[f.key] = values[f.key] || '';
      });
      const res = await createLead(payload, { website: honeypot.current, elapsedMs: startedAt.current ? Date.now() - startedAt.current : 0 });
      // O evento Lead do Pixel é disparado na página de confirmação (uma vez por cadastro novo), com o mesmo eventId da API de Conversões.
      try { sessionStorage.setItem('fc_lp_result', JSON.stringify({ code: res.code, name: res.name, isNew: !!res.isNew, eventId: res.eventId || '' })); } catch { /* ignore */ }
      navigate('/cortesia/confirmacao');
    } catch (e) {
      setDuplicate(e.status === 409);
      setError(e.message || 'Não foi possível concluir agora. Tente novamente.');
      setSubmitting(false);
    }
  };

  return { steps, idx, step, isLast, stepFields, setStepIdx, values, set, touched, blur, errors, submitting, error, duplicate, next, submit, honeypot };
};

const inputCls = 'w-full h-12 rounded-xl border bg-white px-4 text-[16px] text-[#0f1c3a] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#FFE000] focus:border-[#FFE000] transition-shadow';

const Field = ({ id, label, error, children }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-semibold text-[#0f1c3a] mb-1.5">{label}</label>
    {children}
    <p id={`${id}-err`} role="alert" className={`text-[13px] mt-1 min-h-[1.1rem] ${error ? 'text-red-600' : 'text-transparent'}`}>{error || '.'}</p>
  </div>
);

const Choice = ({ name, options, value, onChange, error, legend }) => (
  <fieldset className="mb-1">
    <legend className="text-sm font-semibold text-[#0f1c3a] mb-2">{legend}</legend>
    <div className="space-y-2">
      {options.map((o) => (
        <label key={o.v} className={`flex items-center gap-3 min-h-[48px] rounded-xl border px-4 cursor-pointer transition-colors ${value === o.v ? 'border-[#FFE000] bg-[#FFF9CC]' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
          <input type="radio" name={name} value={o.v} checked={value === o.v} onChange={() => onChange(o.v)} className="sr-only peer" />
          <span aria-hidden="true" className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${value === o.v ? 'border-[#0c2a5c] bg-[#0c2a5c]' : 'border-slate-300'}`}>
            {value === o.v && <span className="h-2 w-2 rounded-full bg-white" />}
          </span>
          <span className="text-[15px] text-[#0f1c3a]">{o.label}</span>
        </label>
      ))}
    </div>
    <p role="alert" className={`text-[13px] mt-1 min-h-[1.1rem] ${error ? 'text-red-600' : 'text-transparent'}`}>{error || '.'}</p>
  </fieldset>
);

const STEP_TITLES = { 1: 'Seus dados', 2: 'Sua viagem' };

const FormCard = ({ form, privacyUrl, idp, priceLabel, diarias }) => {
  const { steps, idx, isLast, stepFields, setStepIdx, values, set, touched, blur, errors, submitting, error, duplicate, next, submit, honeypot } = form;
  const show = (k) => (touched[k] ? errors[k] : '');
  const stepRef = useRef(null);
  useEffect(() => { if (idx > 0 && stepRef.current && !reducedMotion()) stepRef.current.scrollIntoView({ block: 'nearest' }); }, [idx]);

  const renderField = (f) => {
    const id = `${idp}-${f.key}`;
    if (f.type === 'name') {
      return (
        <Field key={f.key} id={id} label={f.label} error={show('name')}>
          <input id={id} className={`${inputCls} ${show('name') ? 'border-red-400' : 'border-slate-200'}`} type="text" autoComplete="given-name"
            autoCapitalize="words" enterKeyHint="next" placeholder={f.placeholder} value={values.name} aria-describedby={`${id}-err`}
            aria-invalid={!!show('name')} onChange={(e) => set('name', e.target.value)} onBlur={() => blur('name')} />
        </Field>
      );
    }
    if (f.type === 'phone') {
      return (
        <Field key={f.key} id={id} label={f.label} error={show('whatsapp')}>
          <input id={id} className={`${inputCls} ${show('whatsapp') ? 'border-red-400' : 'border-slate-200'}`} type="tel" inputMode="tel" autoComplete="tel-national"
            enterKeyHint="next" placeholder={f.placeholder} value={values.whatsapp} aria-describedby={`${id}-err`} aria-invalid={!!show('whatsapp')}
            onChange={(e) => set('whatsapp', maskPhone(e.target.value))} onBlur={() => blur('whatsapp')} />
        </Field>
      );
    }
    if (f.type === 'cityuf') {
      return (
        <div key={f.key} className="grid grid-cols-[1fr_92px] gap-3">
          <Field id={`${id}-city`} label={f.label} error={show('city')}>
            <input id={`${id}-city`} className={`${inputCls} ${show('city') ? 'border-red-400' : 'border-slate-200'}`} type="text" autoComplete="address-level2"
              autoCapitalize="words" enterKeyHint="done" placeholder={f.placeholder} value={values.city} aria-describedby={`${id}-city-err`}
              aria-invalid={!!show('city')} onChange={(e) => set('city', e.target.value)} onBlur={() => blur('city')} />
          </Field>
          <Field id={`${id}-uf`} label={<span className="opacity-0 select-none" aria-hidden="true">UF</span>} error={show('uf')}>
            <select id={`${id}-uf`} aria-label="Estado" className={`${inputCls} px-2 ${show('uf') ? 'border-red-400' : 'border-slate-200'}`} autoComplete="address-level1" value={values.uf}
              aria-describedby={`${id}-uf-err`} onChange={(e) => set('uf', e.target.value)} onBlur={() => blur('uf')}>
              <option value="">UF</option>
              {UFS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </Field>
        </div>
      );
    }
    if (f.type === 'choice') {
      return <Choice key={f.key} legend={f.label} name={id} options={f.options || []} value={values[f.key] || ''} onChange={(v) => set(f.key, v)} error={show(f.key)} />;
    }
    return (
      <Field key={f.key} id={id} label={f.label} error={show(f.key)}>
        <input id={id} className={`${inputCls} ${show(f.key) ? 'border-red-400' : 'border-slate-200'}`} type="text" maxLength={200} autoComplete="off"
          placeholder={f.placeholder} value={values[f.key] || ''} aria-describedby={`${id}-err`} aria-invalid={!!show(f.key)}
          onChange={(e) => set(f.key, e.target.value)} onBlur={() => blur(f.key)} />
      </Field>
    );
  };

  return (
    <div className="rounded-3xl bg-white p-5 sm:p-6 shadow-xl text-left" data-form-card>
      {steps.length > 1 && (
        <div className="flex items-center justify-center gap-3 mb-4" aria-label={`Etapa ${idx + 1} de ${steps.length}`}>
          {steps.map((n, i) => (
            <div key={n} className={`flex items-center gap-2 text-sm font-semibold ${idx === i ? 'text-[#0c2a5c]' : 'text-slate-500'}`}>
              <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs ${idx > i ? 'bg-emerald-500 text-white' : idx === i ? 'bg-[#0c2a5c] text-white' : 'bg-slate-100 text-slate-400'}`}>
                {idx > i ? <Check className="h-4 w-4" /> : i + 1}
              </span>
              {STEP_TITLES[n]}
            </div>
          ))}
        </div>
      )}

      <form noValidate onSubmit={(e) => { e.preventDefault(); if (isLast) submit(); else next(); }}>
        <div className="absolute -left-[9999px] h-0 w-0 overflow-hidden" aria-hidden="true">
          <label>Site<input type="text" name="website" tabIndex={-1} autoComplete="off" onChange={(e) => { honeypot.current = e.target.value; }} /></label>
        </div>

        <div ref={stepRef}>
          {stepFields.map(renderField)}

          {isLast && (
            <>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={values.consent} onChange={(e) => set('consent', e.target.checked)} onBlur={() => blur('consent')}
                  className="mt-0.5 h-5 w-5 shrink-0 rounded border-slate-300 accent-[#0c2a5c]" aria-describedby={`${idp}-consent-err`} />
                <span className="text-[13px] leading-snug text-slate-600">
                  Aceito receber contato do FériasClube pelo WhatsApp sobre esta campanha e estou de acordo com a{' '}
                  <Link to={privacyUrl} target="_blank" className="underline text-[#0c2a5c] font-semibold">Política de Privacidade</Link>.
                </span>
              </label>
              <p id={`${idp}-consent-err`} role="alert" className={`text-[13px] mt-1 min-h-[1.1rem] ${show('consent') ? 'text-red-600' : 'text-transparent'}`}>{show('consent') || '.'}</p>
              {error && (
                <p role="alert" className={`mb-3 rounded-xl border px-4 py-3 text-sm ${duplicate ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-red-50 border-red-200 text-red-700'}`}>
                  {duplicate && <strong className="block">Você já está cadastrado(a)!</strong>}{error}
                </p>
              )}
            </>
          )}

          <button type="submit" disabled={submitting} aria-busy={submitting}
            className="mt-1 w-full min-h-[58px] py-2 px-3 rounded-xl font-extrabold text-[13px] sm:text-[15px] leading-tight tracking-wide flex items-center justify-center gap-2 text-center disabled:opacity-80 transition-transform active:scale-[0.99] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#0c2a5c]/30"
            style={{ backgroundColor: YELLOW, color: NAVY_DK }}>
            {isLast
              ? (submitting
                ? <><Loader2 className="h-5 w-5 animate-spin" /> GERANDO SEU CÓDIGO…</>
                : <span>GARANTIR MEU CÓDIGO<br />{diarias} DIÁRIAS DE {priceLabel}/PESSOA</span>)
              : <><span>QUERO MINHAS {diarias} DIÁRIAS<br />DE {priceLabel}/PESSOA</span><ArrowRight className="h-5 w-5 shrink-0" /></>}
          </button>
          {idx > 0 && (
            <button type="button" onClick={() => setStepIdx(idx - 1)} disabled={submitting} className="mt-2 w-full h-11 text-sm font-semibold text-slate-500 hover:text-[#0c2a5c]">
              ← Voltar
            </button>
          )}
        </div>
      </form>

      <p className="mt-3 flex items-start gap-2 text-[12px] leading-snug text-slate-500">
        <Lock className="h-4 w-4 shrink-0 mt-px" /> Seus dados estão seguros. Não pedimos CPF, não enviamos spam e respeitamos sua privacidade.
      </p>
    </div>
  );
};

// --------------------------------------------------------------------- seções
const TopBar = () => (
  <div className="relative z-20 bg-[#081c40] text-white">
    <div className="mx-auto max-w-6xl px-4 h-11 flex items-center justify-between gap-2 text-[12px] sm:text-[13px]">
      <span className="text-white/70 hidden sm:inline">Já recebeu uma cortesia?</span>
      <div className="flex w-full sm:w-auto items-center justify-between sm:justify-end gap-2">
        <Link to="/cortesia/voucher?acao=desbloquear" className="rounded-full px-3 py-1.5 font-bold text-[#081c40] transition-[filter] hover:brightness-95" style={{ backgroundColor: YELLOW }}>Desbloquear minha cortesia</Link>
        <Link to="/cortesia/voucher?acao=retomar" className="rounded-full border border-white/25 px-3 py-1.5 font-semibold hover:bg-white/10 transition-colors">Já desbloqueei minha cortesia</Link>
      </div>
    </div>
  </div>
);

// A LP pede a logo sem as margens transparentes (&trim=1), senão ela aparece minúscula.
const trimmed = (u) => (u && u.includes('/api/settings/asset/') ? `${u}&trim=1` : u);

const Logo = ({ logo, lazy }) => (
  <a href="/" aria-label="FériasClube — ir para a página inicial" className="inline-block pointer-events-auto">
    {logo
      ? <img src={trimmed(logo)} alt="FériasClube" className="h-11 sm:h-12 w-auto" width="160" height="48" loading={lazy ? 'lazy' : 'eager'} decoding="async" />
      : <span className="font-extrabold text-xl text-white tracking-tight">Férias<span style={{ color: YELLOW }}>Clube</span></span>}
  </a>
);

const PriceCard = ({ price }) => (
  <div className="rounded-2xl border border-[#FFE000]/40 bg-[#0d2f66] px-5 py-4">
    <p className="text-[11px] sm:text-xs font-bold uppercase tracking-wider text-white/80">Com diárias promocionais de</p>
    <p className="text-[40px] leading-none font-extrabold mt-1" style={{ color: YELLOW }}>{price}</p>
    <p className="text-sm text-white/85 mt-1">por pessoa/dia na baixa temporada</p>
  </div>
);

const BenefitStrip = ({ social }) => (
  <div className="bg-white border-b border-slate-100">
    <div className="mx-auto max-w-6xl px-4 py-5">
      <ul className="grid grid-cols-3 gap-2 text-center">
        {[[Palmtree, 'Destinos incríveis'], [Wallet, 'Mais economia nas suas férias'], [Users, 'Viagens em família']].map(([Icon, t]) => (
          <li key={t} className="flex flex-col items-center gap-2 text-[12px] sm:text-sm font-semibold text-[#0c2a5c] leading-tight">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#FFF3C4]"><Icon className="h-5 w-5" /></span>{t}
          </li>
        ))}
      </ul>
      {social && (
        <p className="mt-4 text-center text-[13px] text-slate-600 flex items-center justify-center gap-1.5">
          <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-600" /> <span>Mais de <strong>{Number(social.count).toLocaleString('pt-BR')}</strong> pessoas já validaram a cortesia FériasClube</span>
        </p>
      )}
    </div>
  </div>
);

const Destinos = ({ destinos }) => {
  const ref = useRef(null);
  if (!destinos.length) return null;
  const go = (dir) => {
    const el = ref.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: reducedMotion() ? 'auto' : 'smooth' });
  };
  return (
    <section className="bg-[#F5F8FC] py-12 sm:py-16" aria-labelledby="sec-destinos">
      <div className="mx-auto max-w-6xl px-5">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 id="sec-destinos" className="text-2xl sm:text-3xl font-extrabold text-[#0c2a5c] leading-tight max-w-xl">Suas próximas férias podem estar mais perto do que você imagina.</h2>
            <p className="mt-2 text-slate-600 text-[15px]">Praias, viagens em família, descanso e economia na hospedagem.</p>
          </div>
          <div className="hidden sm:flex gap-2">
            <button type="button" onClick={() => go(-1)} aria-label="Destino anterior" className="h-11 w-11 rounded-full border border-slate-300 bg-white flex items-center justify-center hover:bg-slate-50"><ChevronLeft className="h-5 w-5" /></button>
            <button type="button" onClick={() => go(1)} aria-label="Próximo destino" className="h-11 w-11 rounded-full border border-slate-300 bg-white flex items-center justify-center hover:bg-slate-50"><ChevronRight className="h-5 w-5" /></button>
          </div>
        </div>
        <div ref={ref} tabIndex={0} aria-label="Destinos da campanha" className="mt-6 -mx-5 px-5 flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-px-5 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {destinos.map((d, i) => (
            <figure key={d.id || d.nome} className="snap-start shrink-0 w-[78%] sm:w-[46%] lg:w-[31%] rounded-2xl overflow-hidden bg-white shadow-sm border border-slate-100">
              <img src={optimizedImage(d.fotos[0], 768)} srcSet={optimizedSrcSet(d.fotos[0], [480, 768, 1080])} sizes="(min-width:1024px) 31vw, (min-width:640px) 46vw, 78vw"
                alt={`Praia em ${d.cidade || d.nome}`} width="768" height="576" loading="lazy" decoding="async" className="w-full aspect-[4/3] object-cover" />
              <figcaption className="flex items-center gap-2 px-4 py-3 text-sm font-semibold text-[#0c2a5c]">
                <MapPin className="h-4 w-4 shrink-0" style={{ color: '#d99a00' }} /> {d.cidade || d.nome}
              </figcaption>
            </figure>
          ))}
        </div>
        <p className="mt-3 text-[12px] text-slate-600">Destinos participantes da campanha.</p>
      </div>
    </section>
  );
};

const Steps = () => {
  const steps = [
    [FileText, 'Faça seu cadastro', 'Preencha seus dados e informe seu interesse em viajar.'],
    [Ticket, 'Receba seu código', 'Após o cadastro, o sistema gera seu código individual de participação.'],
    [UserCheck, 'Converse com um consultor', 'Apresente seu código durante um atendimento presencial ou online com um consultor FériasClube autorizado.'],
    [Gift, 'Receba as orientações sobre seu voucher', 'O consultor valida sua participação e realiza a entrega do voucher conforme as condições da campanha.'],
  ];
  return (
    <section className="py-12 sm:py-16 text-white" style={{ backgroundColor: NAVY }} aria-labelledby="sec-como">
      <div className="mx-auto max-w-6xl px-5">
        <h2 id="sec-como" className="text-2xl sm:text-3xl font-extrabold text-center">Como funciona a participação</h2>
        <ol className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map(([Icon, t, d], i) => (
            <li key={t} className="flex gap-4 lg:flex-col lg:items-center lg:text-center">
              <span className="relative flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white text-[#0c2a5c]">
                <Icon className="h-6 w-6" />
                <span className="absolute -top-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full text-xs font-extrabold" style={{ backgroundColor: YELLOW, color: NAVY_DK }}>{i + 1}</span>
              </span>
              <div><h3 className="font-bold text-[17px]">{t}</h3><p className="mt-1 text-[14px] leading-snug text-white/80">{d}</p></div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};

const Benefits = ({ cfg }) => {
  const items = [
    [Sun, `Até ${cfg.diariasMax} diárias promocionais`, 'em destinos do litoral brasileiro'],
    [Wallet, `Diárias de ${cfg.priceLabel}`, 'por pessoa na baixa temporada'],
    [Users, 'Viagem em família ou com amigos', 'mais momentos especiais juntos'],
    [Home, 'Não é necessário ser associado', 'para utilizar o voucher promocional'],
    [UserCheck, 'Atendimento por consultores autorizados', 'presencial ou online'],
    [ShieldCheck, 'Condições de hospedagem promocional', 'conforme as regras da campanha'],
  ];
  return (
    <section className="bg-white py-12 sm:py-16" aria-labelledby="sec-benef">
      <div className="mx-auto max-w-6xl px-5">
        <h2 id="sec-benef" className="text-2xl sm:text-3xl font-extrabold text-[#0c2a5c] text-center">Benefícios da campanha</h2>
        <ul className="mt-8 grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {items.map(([Icon, t, d]) => (
            <li key={t} className="rounded-2xl border border-slate-100 bg-[#FAFBFD] p-4 sm:p-5">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FFF3C4] text-[#8a6100]"><Icon className="h-5 w-5" /></span>
              <h3 className="mt-3 font-bold text-[15px] text-[#0c2a5c] leading-snug">{t}</h3>
              <p className="mt-1 text-[13px] text-slate-600 leading-snug">{d}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

const Faq = ({ cfg }) => {
  const items = [
    ['O voucher é gratuito?', 'A solicitação e a participação na campanha não exigem pagamento pelo voucher. As diárias de hospedagem são cobradas conforme as condições promocionais.'],
    ['Preciso ser associado ao FériasClube?', 'Não. A utilização do voucher promocional não exige associação.'],
    ['Posso escolher qualquer praia?', 'A utilização depende dos destinos participantes, das regras da campanha e da disponibilidade de hospedagem.'],
    ['Como recebo meu voucher?', 'Após o cadastro, você recebe um código de participação para apresentar a um consultor autorizado, presencialmente ou online.'],
    ['O cadastro já garante minha hospedagem?', 'Não. O cadastro não representa reserva, voucher emitido ou garantia de disponibilidade.'],
    ['Até quando posso utilizar?', `Conforme as condições vigentes da campanha, até ${cfg.validadeAte}.`],
    ['Qual é o valor da hospedagem?', `Na baixa temporada, a diária promocional é de ${cfg.priceLabel} por pessoa, com cobrança mínima equivalente a ${cfg.minPessoas} pessoas e demais condições aplicáveis.`],
  ];
  return (
    <section className="bg-[#F5F8FC] py-12 sm:py-16" aria-labelledby="sec-faq">
      <div className="mx-auto max-w-3xl px-5">
        <h2 id="sec-faq" className="text-2xl sm:text-3xl font-extrabold text-[#0c2a5c] text-center">Perguntas frequentes</h2>
        <div className="mt-8 space-y-3">
          {items.map(([q, a]) => (
            <details key={q} className="group rounded-2xl border border-slate-200 bg-white">
              <summary className="flex min-h-[56px] cursor-pointer list-none items-center justify-between gap-4 px-5 py-3 font-semibold text-[#0c2a5c] [&::-webkit-details-marker]:hidden">
                {q}<ChevronDown className="h-5 w-5 shrink-0 transition-transform group-open:rotate-180" />
              </summary>
              <p className="px-5 pb-4 text-[15px] leading-relaxed text-slate-600">{a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
};

// ----------------------------------------------------------------------- página
const Landing = () => {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [cfg, setCfg] = useState(DEFAULTS);
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const form = useLeadForm(navigate, cfg.formFields || DEFAULT_FIELDS);
  // No celular a foto do hero vive numa camada fora do React (criada no index.html) pra o LCP não depender do JS.
  const [pimg] = useState(() => typeof document !== 'undefined' && !!document.getElementById('lp-pimg'));
  const persistent = pimg && !isDesktop;
  useEffect(() => {
    const el = document.getElementById('lp-pimg');
    if (el) el.style.display = isDesktop ? 'none' : '';
  }, [isDesktop]);
  useEffect(() => () => {
    const el = document.getElementById('lp-pimg');
    if (el) el.remove();
    document.documentElement.classList.remove('lp-pimg');
  }, []);
  const heroCtaRef = useRef(null);
  const formRef = useRef(null);
  const finalCtaRef = useRef(null);
  const [sticky, setSticky] = useState(false);
  const flags = useRef({ hero: true, form: false, final: false, kb: false });

  useEffect(() => {
    setMeta({
      title: 'Voucher Cortesia FériasClube — diárias promocionais no litoral',
      description: 'Cadastre-se para participar da campanha Voucher Cortesia FériasClube e descubra como aproveitar diárias promocionais em destinos do litoral brasileiro.',
    });
    trackStep('view');
    let alive = true;
    getLpConfig().then((c) => {
      if (!alive) return;
      setCfg({ ...DEFAULTS, ...c });
      initPixels(c.tracking || {});
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // Botão fixo (só celular): some quando o botão do hero ou o formulário está na tela ou o teclado está aberto.
  useEffect(() => {
    if (isDesktop) { setSticky(false); return undefined; }
    const update = () => setSticky(!flags.current.hero && !flags.current.form && !flags.current.final && !flags.current.kb);
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { flags.current[e.target.dataset.flag] = e.isIntersecting; });
      update();
    }, { threshold: 0.15 });
    [heroCtaRef, formRef, finalCtaRef].forEach((r) => r.current && io.observe(r.current));
    const isField = (t) => t && /^(INPUT|SELECT|TEXTAREA)$/.test(t.tagName);
    const onIn = (e) => { if (isField(e.target)) { flags.current.kb = true; update(); } };
    const onOut = () => { flags.current.kb = false; update(); };
    document.addEventListener('focusin', onIn);
    document.addEventListener('focusout', onOut);
    return () => { io.disconnect(); document.removeEventListener('focusin', onIn); document.removeEventListener('focusout', onOut); };
  }, [isDesktop]);

  const goToForm = () => {
    const el = formRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
    window.setTimeout(() => { const f = el.querySelector('input[type="text"]'); if (f) f.focus({ preventScroll: true }); }, reducedMotion() ? 0 : 450);
  };

  // Toque no botão da 1ª tela (HTML) antes do React carregar: rola até o formulário assim que a página monta.
  useEffect(() => {
    if (window.__lpGo) {
      window.__lpGo = 0;
      const t = window.setTimeout(() => goToForm(), 250);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, []);

  const logo = settings.logoLight;
  const price = cfg.priceLabel;

  return (
    <div className="text-[#0f1c3a] font-sans antialiased" style={{ paddingBottom: sticky ? 'calc(92px + env(safe-area-inset-bottom))' : 0 }}>
      <TopBar />

      {/* HERO */}
      <header className="relative text-white" style={{ backgroundColor: persistent ? 'transparent' : NAVY_DK }}>
        <div className="relative h-[52svh] min-h-[340px] max-h-[520px] lg:absolute lg:inset-0 lg:h-auto lg:max-h-none lg:min-h-0">
          {!persistent && (
            <>
              <img src={optimizedImage(HERO_SRC, 768)} srcSet={optimizedSrcSet(HERO_SRC)} sizes={HERO_SIZES} alt="Praia de Bombinhas, Santa Catarina"
                width="1080" height="720" fetchpriority="high" decoding="async" className="absolute inset-0 h-full w-full object-cover" style={{ filter: 'saturate(1.12) contrast(1.04)' }} />
              {/* topo da foto 100% limpo: só escurece onde fica o texto (embaixo no celular, à esquerda no desktop) */}
              <div className="absolute inset-0 lg:hidden" style={{ background: 'linear-gradient(to top,#081c40 0%,rgba(8,28,64,.88) 24%,rgba(8,28,64,.5) 44%,rgba(8,28,64,0) 64%)' }} />
              <div className="absolute inset-0 hidden lg:block" style={{ background: 'linear-gradient(to right,rgba(8,28,64,.8) 0%,rgba(8,28,64,.5) 34%,rgba(8,28,64,0) 62%)' }} />
            </>
          )}
          <div className="absolute inset-x-0 top-0 z-20 mx-auto flex max-w-6xl justify-center px-4 pt-4 pointer-events-none"><Logo logo={logo} /></div>
        </div>

        <div className="relative z-10 mx-auto max-w-6xl px-5 -mt-44 pb-8 lg:mt-0 lg:grid lg:grid-cols-[minmax(0,1fr)_420px] lg:items-center lg:gap-10 lg:pt-28 lg:pb-14">
          <div className="max-w-xl">
            <span className="inline-block rounded-md px-3 py-1 text-[11px] font-extrabold uppercase tracking-wider" style={{ backgroundColor: YELLOW, color: NAVY_DK }}>Voucher Cortesia</span>
            <h1 className="mt-3 text-[28px] sm:text-4xl lg:text-5xl font-extrabold leading-[1.1] [text-wrap:balance] [text-shadow:0_2px_14px_rgba(0,0,0,.45)]">
              Que tal <span className="underline decoration-[#FFE000] decoration-[5px] underline-offset-[6px] [text-decoration-skip-ink:none]">4 dias de hospedagem</span> na Praia para até 5 pessoas?
            </h1>
            <p className="mt-3 text-[15px] sm:text-lg leading-snug text-white/90">
              <span className="sm:hidden">Cadastre-se na campanha Voucher Cortesia FériasClube e aproveite até {cfg.diariasMax} diárias promocionais no litoral brasileiro.</span>
              <span className="hidden sm:inline">Cadastre-se para participar da campanha Voucher Cortesia FériasClube e descubra como aproveitar até {cfg.diariasMax} diárias promocionais em destinos incríveis do litoral brasileiro.</span>
            </p>
            <div className="mt-5"><PriceCard price={price} /></div>
            <button ref={heroCtaRef} data-flag="hero" type="button" onClick={goToForm}
              className="lg:hidden mt-5 w-full min-h-[58px] py-2 px-3 rounded-xl font-extrabold text-[13px] sm:text-[15px] leading-tight tracking-wide flex items-center justify-center gap-2 text-center shadow-lg active:scale-[0.99] transition-transform focus:outline-none focus-visible:ring-4 focus-visible:ring-white/50"
              style={{ backgroundColor: YELLOW, color: NAVY_DK }}>
              <span>QUERO MINHAS {cfg.diariasMax} DIÁRIAS<br />DE {cfg.priceLabel}/PESSOA</span><ArrowRight className="h-5 w-5 shrink-0" />
            </button>
            <p className="mt-2 text-[12px] text-white/70 lg:hidden">Cadastro rápido, sem custo e sem CPF.</p>
          </div>
          {isDesktop && (
            <div className="mt-0">
              <p className="mb-3 text-center text-xl font-extrabold">Quero meu código de participação</p>
              <FormCard form={form} privacyUrl={cfg.privacyUrl} idp="d" priceLabel={cfg.priceLabel} diarias={cfg.diariasMax} />
            </div>
          )}
        </div>
      </header>

      <BenefitStrip social={cfg.social} />

      {/* FORMULÁRIO (celular): logo depois do hero, na segunda tela */}
      {!isDesktop && (
        <section ref={formRef} data-flag="form" id="cadastro" className="px-4 py-8" style={{ backgroundColor: '#FFF7DB', scrollMarginTop: 8 }} aria-labelledby="sec-form">
          <div className="mx-auto max-w-md">
            <h2 id="sec-form" className="text-center text-2xl font-extrabold text-[#0c2a5c]">Agora é com você!</h2>
            <p className="mt-1 mb-4 text-center text-[15px] text-slate-700">Cadastre-se e receba seu código de participação.</p>
            <FormCard form={form} privacyUrl={cfg.privacyUrl} idp="m" priceLabel={cfg.priceLabel} diarias={cfg.diariasMax} />
          </div>
        </section>
      )}

      <Destinos destinos={cfg.destinos} />
      <Steps />
      <Benefits cfg={cfg} />
      <Faq cfg={cfg} />

      {/* CHAMADA FINAL */}
      <section className="py-14 text-white" style={{ backgroundColor: NAVY_DK }} aria-labelledby="sec-final">
        <div className="mx-auto max-w-3xl px-5 text-center">
          <h2 id="sec-final" className="text-2xl sm:text-3xl font-extrabold">Suas próximas férias começam com uma oportunidade.</h2>
          <p className="mt-3 text-white/85 text-[15px] sm:text-lg">Cadastre-se, receba seu código de participação e conheça as condições para aproveitar hospedagens promocionais com o FériasClube.</p>
          <button ref={finalCtaRef} data-flag="final" type="button" onClick={isDesktop ? () => window.scrollTo({ top: 0, behavior: reducedMotion() ? 'auto' : 'smooth' }) : goToForm}
            className="mt-6 inline-flex h-14 items-center justify-center gap-2 rounded-xl px-8 font-extrabold tracking-wide focus:outline-none focus-visible:ring-4 focus-visible:ring-white/50"
            style={{ backgroundColor: YELLOW, color: NAVY_DK }}>
            QUERO PARTICIPAR <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </section>

      {/* RODAPÉ */}
      <footer className="bg-white px-5 py-8 text-[12px] leading-relaxed text-slate-500">
        <div className="mx-auto max-w-4xl">
          <details className="mb-4">
            <summary className="cursor-pointer font-semibold text-slate-600">Condições da campanha</summary>
            <ul className="mt-2 list-disc pl-5 space-y-1">
              <li>O cadastro gera apenas um código de participação. O código não é voucher, reserva nem garantia de disponibilidade.</li>
              <li>O voucher é entregue exclusivamente por consultor FériasClube autorizado, após a validação da participação e a conferência de identidade.</li>
              <li>Válido somente para a baixa temporada, sujeito à disponibilidade e às regras da campanha, até {cfg.validadeAte}.</li>
              <li>As diárias de hospedagem são pagas conforme as condições promocionais: {cfg.priceLabel} por pessoa/dia, com cobrança mínima equivalente a {cfg.minPessoas} pessoas.</li>
              <li>A campanha não exige associação ao FériasClube.</li>
            </ul>
          </details>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Logo logo={settings.logoDark} lazy />
            <nav className="flex gap-4 font-semibold text-slate-600">
              <Link to={cfg.privacyUrl} className="hover:underline">Política de Privacidade</Link>
              <Link to="/cortesia/voucher?acao=retomar" className="hover:underline">Já desbloqueei minha cortesia</Link>
            </nav>
          </div>
          <p className="mt-4">© {new Date().getFullYear()} FériasClube. Todos os direitos reservados.</p>
        </div>
      </footer>

      {/* BOTÃO FIXO (celular) */}
      {!isDesktop && (
        <div aria-hidden={!sticky} className={`fixed inset-x-0 bottom-0 z-40 px-4 pt-3 transition-all duration-200 ${sticky ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0 pointer-events-none'}`}
          style={{ paddingBottom: 'calc(12px + env(safe-area-inset-bottom))', background: 'linear-gradient(to top, rgba(8,28,64,0.96) 70%, rgba(8,28,64,0))' }}>
          <button type="button" tabIndex={sticky ? 0 : -1} onClick={goToForm}
            className="w-full min-h-[58px] py-2 px-3 rounded-xl font-extrabold text-[13px] leading-tight tracking-wide flex items-center justify-center gap-2 text-center shadow-xl active:scale-[0.99] transition-transform"
            style={{ backgroundColor: YELLOW, color: NAVY_DK }}>
            <span>QUERO MINHAS {cfg.diariasMax} DIÁRIAS<br />DE {cfg.priceLabel}/PESSOA</span><ArrowRight className="h-5 w-5 shrink-0" />
          </button>
        </div>
      )}
    </div>
  );
};

export default Landing;
